import { unifiedBAt } from "../../../src/game/income-growth.ts";
import { allocateBuildingCosts, buildingWeights } from "../../../src/game/building-cost-matrix.ts";
/**
 * 通关配置的数据模型与派生计算。
 *
 * 公式价跟服务端 src/game/city-costs.ts 同口径（serverFormulaPerLevel / formulaTotal）：
 * 目标造价（实际总造价优先，否则 骰次 × E × B × ρ 取整到百）按系统内置星形状（src/game/star-shape.ts）摊到每级，N 栋同价。
 * 关卡里的 star 字段是库里那一列，算价不读它（leo 2026-10-09）。
 * E / ρ 必须传本版本的骰子参数——落到 DEFAULT_GLOBALS 会算出另一套价（2026-10-08 1201 显示 837、实际 295）。
 * 设计稿 engine.jsx 的「各栋 1★ 金额（bldInit）× 星级倍率」只剩 bldInit 这份数据，结算与展示都不再用它。
 */
import { parseCostGrowth, costGrowthAt } from "../../../src/game/cost-growth.ts";
import { builtinStarShape } from "../../../src/game/star-shape.ts";
import { costSourceKind, costSourceRival } from "./costSource.ts";
import { fmtFull, fromBig, roundHundredFrac, roundPrettyFrac, toBig, type Money } from "./big.ts";
import { ENV_SCALE, bFromPrice, eMilliOf, envProduct, incomeGrowthAt, rhoBpsOf, targetRollsAt } from "./curve.ts";

export type { Money };

/** 每骰产出锚与消耗系数，在总造价页配置 */
export interface Globals {
  unifiedB?: boolean; algorithmAnchorB?: Money | null; costCurve?: string; E: number; rho: number; incomeGrowthBps?: number; incomeAnchorB?: Money | null; targetRollsBase?: number; targetRollsGrowthBps?: number;
  /** 本版本金额口径（cfg_version.economy_model）：缺省 / 1 = B 数 × 运行时 B；2 = 「几骰」系数 q × 刻度 × 本关 g（docs/economy-g-model.md §2.4） */
  economyModel?: 1 | 2;
  /** model 2 的版本公共刻度 num/den */
  moneyScaleNum?: number | null;
  moneyScaleDen?: number | null;
}
export const DEFAULT_GLOBALS: Globals = { E: 3.1, rho: 0.9 };

/** 实际造价与目标造价的允许偏差 */
export const DEV_LIMIT = 0.03;

export interface Building {
  code: string; name: string; nameI18n?: Record<string, string>; main: boolean;
  /** 摆位：相对关卡中心点的偏移，单位是建造底图坐标系的像素（x 右为正 / y 下为正）。0 = 未摆位，压在中心 */
  x: number;
  y: number;
}

/** 单元格坐标 key：建筑序号_星级，均从 1 起 */
export type CellKey = `${number}_${number}`;
export const cellKey = (b: number, l: number) => `${b}_${l}` as CellKey;
/** 逐格造价。经济数值没有上限：安全整数内是 number，超出是数字串（见 big.ts） */
export type Grid = Partial<Record<CellKey, Money | null>>;

export interface Level {
  id: number;
  /**
   * 稳定身份（UUID）。id 是位置，插一关后面全体 +1；协同编辑合并两个人的关卡改动、
   * 自动保存判断「哪几关是新加的」都只认它。新建时本地生成，随建关请求交给服务端
   */
  uid: string;
  /** 英文为默认语言，其余语言由本地化流程回填 */
  name: string;
  stage: string;
  i18n: Record<string, { name?: string; stage?: string }>;
  B: Money | null;
  targetRolls: Money | null;
  targetRollsOverride?: Money | null;
  /**
   * 单掷金币 g（model 2，docs/economy-g-model.md §2.2）：千分之一金币的无损十进制串，原样读写不经过数字。
   * null = 走曲线。model 1 版本里不显示也不改，只原样带着
   */
  gMilli?: string | null;
  costMatrixSeed?: number | null;
  /** 目标骰次的浮动范围（±%），用于给数值一个可接受区间 */
  rollsRange: number | null;
  /** 手填的实际总造价；为空则以算法总造价为目标 */
  actualC: Money | null;
  /** 关卡封面图（横版，选关列表用）。空串 = 未上传 */
  coverImg: string;
  /** 建造页底图（竖版，不含建筑）。空串 = 未上传 */
  bgImg: string;
  N: number;
  M: number;
  buildings: Building[];
  /** 各栋 1★ 的绝对金额，长度 = N */
  bldInit: (Money | null)[];
  /** 星级倍率，长度 = M */
  star: (number | null)[];
  /** 系统（我方实际）逐格造价 */
  sys: Grid;
  /**
   * 逐格素材，键同 Grid 的 `"<建筑>:<星级>"`，**稀疏**——没传过的格子不出现。
   * 这里只有该格自己传过的图；「未上传回退上一星级」是下发给客户端时才做的，
   * 编辑页要能分清「这格没图」和「这格在用上一级的图」。
   */
  /** "栋:星" → 三张图。damaged = 被破坏时的外观，空 = 未上传（下发时回退到 img） */
  assets: Record<string, { img: string; anim: string; damaged: string }>;
  /**
   * 关卡资源三槽位（bg=棋盘背景 / blank=棋盘空白格 / mid=中间建筑）。
   * 每槽 {img?, from?}：img = 本关上传；from = 显式继承来源关。
   * 都缺 = 默认继承上一关。下发给客户端时由服务端沿链解析。
   */
  levelAssets: Record<string, { img?: string; from?: number }>;
  /** 各竞品逐格造价，键为竞品 id；竞品未覆盖到的关卡为空对象 */
  rivals: Record<string, Grid>;
}

export const num = (v: unknown): number | null =>
  v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v);

/** 保留 2 位有效数字：玩家看到 4,300 而不是 4,285 */
export function round2sig(v: number): number {
  if (!Number.isFinite(v) || v === 0) return 0;
  const d = Math.floor(Math.log10(Math.abs(v)));
  const f = 10 ** (d - 1);
  return Math.round(v / f) * f;
}
export function round3sig(v: number): number {
  if (!Number.isFinite(v) || v === 0) return 0;
  const d = Math.floor(Math.log10(Math.abs(v)));
  const f = 10 ** (d - 2);
  return Math.round(v / f) * f;
}

/** 曲线关闭时读原逐关值；清空覆盖值就恢复曲线。参数不合法显示空，不用旧值顶替。没有上限（bigint） */
export function levelTargetRolls(lv: Pick<Level, "id" | "targetRolls" | "targetRollsOverride">, g: Globals = DEFAULT_GLOBALS): bigint | null {
  return targetRollsAt(lv.targetRolls, lv.id, g.targetRollsBase, g.targetRollsGrowthBps, lv.targetRollsOverride);
}

const posBig = (v: unknown): bigint | null => {
  const b = toBig(v);
  return b != null && b > 0n ? b : null;
};

/**
 * 算法总造价：骰次 × E × B × ρ，只读。取整「后两位为 0」（leo 2026-10-16）：
 * ≥100 四舍五入到 100 的整数倍，<100 取整到个位。没填实际总造价时它就是公式价的目标，服务端同口径
 */
export function algorithmB(lv: Level, g: Globals): bigint | null {
  if (!g.unifiedB) return toBig(lv.B);
  const anchor = toBig(g.algorithmAnchorB), override = toBig(g.incomeAnchorB) ?? 0n;
  if (!(override > 0n) && !(anchor != null && anchor > 0n)) return null;
  try { return unifiedBAt(anchor ?? override, lv.id, g.incomeGrowthBps, override); } catch { return null; }
}

export function algoC(lv: Level, g: Globals = DEFAULT_GLOBALS): bigint | null {
  if (g.costCurve) {
    try { const c = parseCostGrowth(g.costCurve); return c ? costGrowthAt(c, lv.id) : null; } catch { return null; }
  }
  const b = algorithmB(lv, g), r = levelTargetRolls(lv, g);
  // model 2：公式价 = 目标骰次 × 本关 g × ρ（g 千分之一金币，与 E_milli × B 同量纲；与服务端 algoCityTotal 同口径）
  // g 没填就算不出来：不能退回旧口径的 E × B（单掷金币版本的 B 早已不对应金币，差出几百倍）
  if (g.economyModel === 2 && toBig(lv.gMilli ?? null) == null) return null;
  const gm = g.economyModel === 2 ? toBig(lv.gMilli ?? null) : null;
  if (gm != null) return gm > 0n && r && r > 0n && rhoBpsOf(g.rho) > 0n ? roundHundredFrac(r * gm * rhoBpsOf(g.rho), ENV_SCALE) : null;
  // E / ρ 没配（新版本存 -1 占位，读进来是负数或 null）就算不出来，不拿默认常量顶上——服务端同样算不出
  return b && r && r > 0n && envOk(g) ? roundHundredFrac(envProduct(r, b, g.E, g.rho), ENV_SCALE) : null;
}
const envOk = (g: Globals) => eMilliOf(g.E) > 0n && rhoBpsOf(g.rho) > 0n;

/**
 * 公式算不出这一关时，缺的是哪一样（按要补的先后）：目标骰次 → B 值 → E / ρ。
 * 填了实际总造价时目标就是它，B 与 E / ρ 不参与。算得出给 null。
 */
export function formulaGap(lv: Level, g: Globals): "rolls" | "b" | "g" | "env" | null {
  const r = levelTargetRolls(lv, g);
  if (!(r != null && r > 0n)) return "rolls";
  if ((lv.costMatrixSeed == null && posBig(lv.actualC) != null) || (g.costCurve && algoC(lv, g) != null)) return null;
  if (g.economyModel === 2) return algoC(lv, g) != null ? null : toBig(lv.gMilli ?? null) == null ? "g" : "env";
  if (algorithmB(lv, g) == null) return "b";
  if (!envOk(g)) return "env";
  return null;
}
export const actualC = (lv: Level) => toBig(lv.actualC);
/** 目标造价：填了实际造价就以它为准，否则用算法造价 */
export const targetC = (lv: Level, g?: Globals) => actualC(lv) ?? algoC(lv, g);

/** 网格求和。**有空格就返回 null**——半张表的合计会被当成真实数字读，比没有更危险 */
export function gridSum(map: Grid, N: number, M: number): { sum: bigint | null; partial: bigint; empty: number } {
  let sum = 0n, empty = 0;
  for (let b = 1; b <= N; b++)
    for (let l = 1; l <= M; l++) {
      const v = toBig(map[cellKey(b, l)]);
      if (v == null) empty++;
      else sum += v;
    }
  return { sum: empty ? null : sum, partial: sum, empty };
}

export const gridRows = (map: Grid, N: number, M: number): (bigint | null)[][] =>
  Array.from({ length: N }, (_, b) => Array.from({ length: M }, (_, l) => toBig(map[cellKey(b + 1, l + 1)])));

/**
 * 服务端公式价（city-costs.ts deriveCityCosts 的镜像）：目标造价（实际总造价优先，否则算法总造价 algoC，已取整到百）
 * 按系统内置星形状摊到每栋的 M 个星级，N 栋同价，每级两位有效数字。目标算不出来给 null。
 * 整条在分数上算完再取一次整：目标 × 星形状 ÷ (N × 星形状合计)，bigint，没有上限。
 */
export function serverFormulaPerLevel(lv: Level, g: Globals = DEFAULT_GLOBALS): bigint[] | null {
  if (!Number.isInteger(lv.M) || lv.M < 1 || !Number.isInteger(lv.N) || lv.N < 1) return null;
  const shape = builtinStarShape(lv.M).map(BigInt);
  const sum = shape.reduce((a, b) => a + b, 0n);
  // 目标：手填总造价优先，否则算法总造价（已按 algoC 取整到百，服务端回退值同口径）
  const n = posBig(lv.actualC) ?? algoC(lv, g);
  if (n == null || !(n > 0n)) return null;
  return shape.map((s) => roundPrettyFrac(n * s, BigInt(lv.N) * sum));
}
/** 金额与随机比例共用同一生成器；没有 seed 的旧版本保持旧规则。 */
export function generatedCostGrid(lv: Level, g: Globals, manual = false): Grid {
  if (lv.costMatrixSeed == null) return overlayGrid(lv, manual ? lv.sys : {}, g);
  const total = manual || g.unifiedB ? posBig(lv.actualC) ?? algoC(lv, g) : algoC(lv, g);
  const out: Grid = {};
  try {
    if (total == null) return out;
    const matrix = allocateBuildingCosts(total, buildingWeights(lv.N, lv.M, lv.uid || lv.id, lv.costMatrixSeed));
    matrix.forEach((column, b) => column.forEach((v, l) => { out[cellKey(b + 1, l + 1)] = fromBig(v); }));
  } catch { /* 参数未齐时保留空格，发布校验给出具体原因。 */ }
  return out;
}
/**
 * 「公式」那套价的整关合计：serverFormulaPerLevel 的逐级价 × N 栋，与资源块 total_cost、实际扣费同口径。
 * 不是 algoC——填了实际总造价时服务端以它为目标，algoC 却不看它；摊开取整后也不再等于目标。
 */
export function formulaTotal(lv: Level, g: Globals = DEFAULT_GLOBALS): bigint | null {
  if (lv.costMatrixSeed != null) return gridSum(generatedCostGrid(lv, g), lv.N, lv.M).sum;
  const per = serverFormulaPerLevel(lv, g);
  return per ? BigInt(lv.N) * per.reduce((a, b) => a + b, 0n) : null;
}
/** 两位有效数字，100 以下原样四舍五入（与服务端 roundPretty 同口径）。number 版，只给小数值的默认曲线用 */
export function roundPretty(v: number): number {
  if (v < 100) return Math.round(v);
  const digits = Math.floor(Math.log10(v)) + 1;
  const unit = 10 ** (digits - 2);
  return Math.round(v / unit) * unit;
}
/**
 * 手填 / 竞品的网格叠在公式价上：填了的格用填的，没填的走公式（leo 2026-09-16：可以填也可以不填）。
 * 公式算不出来时没填的格保持 null（那才是真的缺价）。
 */
export function overlayGrid(lv: Level, grid: Grid, g?: Globals): Grid {
  const matrix = lv.costMatrixSeed != null ? generatedCostGrid(lv, g ?? DEFAULT_GLOBALS) : null;
  const f = matrix ? null : serverFormulaPerLevel(lv, g);
  const out: Grid = {};
  for (let b = 1; b <= lv.N; b++)
    for (let l = 1; l <= lv.M; l++) {
      const k = cellKey(b, l);
      const v = toBig(grid[k]);
      out[k] = v != null ? fromBig(v) : matrix ? matrix[k] ?? null : f ? fromBig(f[l - 1]) : null;
    }
  return out;
}

/** 实际逐格价合计：手动覆盖值叠加公式默认值，与服务端 manual 取价一致。 */
export const effectiveActualTotal = (lv: Level, g: Globals) => gridSum(lv.costMatrixSeed != null ? generatedCostGrid(lv, g, true) : overlayGrid(lv, lv.sys, g), lv.N, lv.M).sum;

export const sysTotal = (lv: Level) => gridSum(lv.sys, lv.N, lv.M).sum;
export const rivalTotal = (lv: Level, rid: string) => {
  const m = lv.rivals[rid];
  return m ? gridSum(m, lv.N, lv.M).sum : null;
};


/**
 * 关卡数量不固定，由后台配置；序号是**位置**而非稳定标识。
 * 在中间插入一关后，其后所有关的 id 都会前移一位——这也是为什么
 * 关卡内的建筑用 code（`${id}-building-${b}`）而不是纯序号来标识。
 */
export function renumber(levels: Level[]): Level[] {
  return levels.map((lv, i) => (lv.id === i + 1 ? lv : { ...lv, id: i + 1 }));
}

/** 新关卡的稳定身份。浏览器里有 crypto.randomUUID；单测的 node 也有 */
export const newUid = () => globalThis.crypto.randomUUID();

/** 新关卡：只带名称与两个关卡参数，成本相关一律留空待填——不替策划编数值 */
export function makeLevel(at: number, fields: {
  name: string;
  i18n?: Level["i18n"];
  B?: Money | null;
  targetRolls?: Money | null;
  targetRollsOverride?: Money | null;
  costMatrixSeed?: number | null;
}, prev?: Level): Level {
  const N = prev?.N ?? 5;
  const M = prev?.M ?? 5;
  return {
    id: at,
    uid: newUid(),
    name: fields.name,
    stage: "",
    i18n: fields.i18n ?? {},
    B: fields.B ?? null,
    targetRolls: fields.targetRolls ?? null,
    targetRollsOverride: fields.targetRollsOverride ?? null,
    costMatrixSeed: fields.costMatrixSeed ?? null,
    rollsRange: prev?.rollsRange ?? 10,
    actualC: null,
    coverImg: "",
    bgImg: "",
    N, M,
    buildings: Array.from({ length: N }, (_, b) => ({ code: `${at}-building-${b + 1}`, name: `Building ${b + 1}`, main: b === N - 1, x: 0, y: 0 })),
    levelAssets: {}, // 新关不配 = 默认继承上一关
    bldInit: Array.from({ length: N }, () => null),
    star: Array.from({ length: M }, () => null),
    sys: {},
    assets: {},
    rivals: {},
  };
}

/**
 * 千分位整数。bigint / 数字串走精确分组（造价、B 到 1e24 也一位不丢）；number 照旧四舍五入。
 * 明细格子放不下时用 big.ts 的 fmtAmount 缩写，完整值放 title。
 */
export const fmt = (n: number | bigint | string | null | undefined) =>
  n == null || (typeof n === "number" && Number.isNaN(n)) ? "—"
    : typeof n === "number" && Number.isSafeInteger(Math.round(n)) ? Math.round(n).toLocaleString("en-US")
    : fmtFull(n);
export const pct = (n: number, d = 1) => `${(n * 100).toFixed(d)}%`;

/**
 * 每关运行时实际用的 B（掷骰产出、发奖、对抗额度都按它折金币），服务端 main-config.ts cityBValue 的镜像：
 *   公式 / 未选：没填实际总造价用库里的 B；填了就按公式价合计反推
 *   手填 / 竞品：按「填了的格 + 没填的走公式」的合计反推
 *   收入膨胀 K：以第 1 关的 B 为锚，第 N 关 × K^(N−1)
 * 算不出来给 null（参数没配齐），界面上显示「—」，不拿默认值顶上。
 */
export function levelRuntimeB(levels: Level[], g: Globals, costSource: string): (bigint | null)[] {
  if (g.unifiedB) return levels.map(lv => algorithmB(lv, g));
  const kind = costSourceKind(costSource);
  const rid = costSourceRival(costSource);
  const base = (lv: Level): bigint | null => {
    const stored = toBig(lv.B);
    const rolls = levelTargetRolls(lv, g);
    const fromTotal = (total: bigint | null) => bFromPrice(total, rolls, g.E, g.rho);
    if (kind === "manual" || kind === "rival") {
      const grid = kind === "manual" ? lv.sys : (rid ? lv.rivals[rid] ?? {} : {});
      if (kind === "manual" && lv.costMatrixSeed != null) return fromTotal(effectiveActualTotal(lv, g)) ?? stored;
      return fromTotal(gridSum(overlayGrid(lv, grid, g), lv.N, lv.M).sum) ?? stored;
    }
    if (lv.costMatrixSeed == null && (posBig(lv.actualC) != null || g.costCurve)) return fromTotal(formulaTotal(lv, g)) ?? stored;
    return stored;
  };
  const bases = levels.map(base);
  const bps = g.incomeGrowthBps ?? 0;
  if (!bps) return bases;
  const anchor = bases[0];
  return bases.map((b, i) => (b == null || anchor == null ? null : incomeGrowthAt(b, anchor, levels[i].id - 1, bps, g.incomeAnchorB)));
}

/** 当前选用的实际价格合计，供骰次估算使用，不能反写配置。 */
export function selectedPriceTotal(lv: Level, g: Globals, source: string): bigint | null {
  const kind = costSourceKind(source);
  if (kind === "formula") return formulaTotal(lv, g);
  if (kind === "manual") return effectiveActualTotal(lv, g);
  const id = costSourceRival(source);
  return kind === "rival" && id ? gridSum(overlayGrid(lv, lv.rivals[id] ?? {}, g), lv.N, lv.M).sum : null;
}
